"use client";

/**
 * GenbaChatWidget — GenbaCompass 傘下アプリ共通の埋め込みチャットウィジェット（正本）
 * ============================================================================
 * 依存: React のみ（framer-motion / lucide-react / next/image / Tailwind 不要）。
 *       スタイルはすべてインライン + 注入した <style> で完結するため、
 *       ホストアプリの CSS 構成に影響されず、Next.js / Vite どちらにも drop-in できる。
 *
 * 使い方（各アプリ側）:
 *   <GenbaChatWidget
 *     appId="anzenai"
 *     endpoint="https://onndphrrqfnogqexpfgw.supabase.co/functions/v1/genbacompass_chat"
 *     anonKey={SUPABASE_ANON_KEY}   // Edge Function 呼び出しに必要
 *   />
 *
 * バックエンドは共有 Edge Function `genbacompass_chat`（appId でボットを出し分ける）。
 * name / greeting / theme は endpoint の GET から取得するため、appId だけで自己設定される。
 *
 * ※このファイルは genba-compass-main が正本。各アプリへはコピーして使う。
 *   更新時は正本を直し、各アプリへ再コピーすること。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ClientConfig {
  appId: string;
  name: string;
  greeting: string;
  theme: { primary: string; accent: string };
  leadCaptureAfterMessages: number;
  ctaUrl: string;
}

export interface GenbaChatWidgetProps {
  /** アプリ識別子（例: "anzenai", "genbasafeai"） */
  appId: string;
  /** 共有 Edge Function の URL */
  endpoint: string;
  /** Supabase anon key（Edge Function 呼び出しの Authorization に使用） */
  anonKey: string;
  /** 任意: 設定取得より前に使うプレースホルダ名 */
  fallbackName?: string;
  /** 任意: 問い合わせCTAメールアドレス */
  contactEmail?: string;
  /** 任意: アシスタントのアバター画像URL（ヘッダ・吹き出し・ランチャーに表示。未指定ならアイコン） */
  avatarUrl?: string;
}

const DEFAULT_CONFIG: ClientConfig = {
  appId: "genbacompass",
  name: "AI アシスタント",
  greeting: "こんにちは！ご質問がありましたらお気軽にどうぞ。",
  theme: { primary: "#2563EB", accent: "#1D4ED8" },
  leadCaptureAfterMessages: 3,
  ctaUrl: "",
};

function generateSessionId(): string {
  return `chat_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** 依存ゼロの最小 Markdown レンダラ（HTML をエスケープしてから限定的な整形のみ適用） */
function renderMarkdown(src: string): string {
  const esc = src
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  const lines = esc.split("\n");
  const html: string[] = [];
  let listType: "ul" | "ol" | null = null;

  const closeList = () => {
    if (listType) {
      html.push(`</${listType}>`);
      listType = null;
    }
  };

  const inline = (t: string) =>
    t
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      // URL は href 属性に入れる前に引用符をエスケープ（属性ブレイクアウト=XSS を防止）。
      // 本文(t)は呼び出し前に < > & をエスケープ済み。スキームは正規表現で https?:// に限定。
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_m, text, url) => {
        const safeUrl = String(url).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
        return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      });

  for (const raw of lines) {
    const line = raw.trimEnd();
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    const ul = line.match(/^\s*[-*]\s+(.*)$/);
    if (ol) {
      if (listType !== "ol") { closeList(); html.push("<ol>"); listType = "ol"; }
      html.push(`<li>${inline(ol[1])}</li>`);
    } else if (ul) {
      if (listType !== "ul") { closeList(); html.push("<ul>"); listType = "ul"; }
      html.push(`<li>${inline(ul[1])}</li>`);
    } else if (line.trim() === "") {
      closeList();
    } else {
      closeList();
      html.push(`<p>${inline(line)}</p>`);
    }
  }
  closeList();
  return html.join("");
}

// --- インライン SVG アイコン（lucide 非依存） ---
const IconChat = ({ color = "#fff" }: { color?: string }) => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);
const IconClose = ({ color = "#fff" }: { color?: string }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconSend = ({ color = "#fff" }: { color?: string }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

export function GenbaChatWidget({
  appId,
  endpoint,
  anonKey,
  fallbackName = "AI アシスタント",
  contactEmail = "support@genbacompass.com",
  avatarUrl,
}: GenbaChatWidgetProps) {
  const [config, setConfig] = useState<ClientConfig>({
    ...DEFAULT_CONFIG,
    appId,
    name: fallbackName,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [sessionId] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = sessionStorage.getItem(`chat_session_${appId}`);
      if (stored) return stored;
      const id = generateSessionId();
      sessionStorage.setItem(`chat_session_${appId}`, id);
      return id;
    }
    return generateSessionId();
  });

  const primary = config.theme.primary;

  // 設定取得（name/greeting/theme）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${endpoint}?appId=${encodeURIComponent(appId)}`, {
          headers: { Authorization: `Bearer ${anonKey}`, apikey: anonKey },
        });
        if (!res.ok) return;
        const data = (await res.json()) as ClientConfig;
        if (!cancelled && data?.appId) setConfig(data);
      } catch {
        // 取得失敗時は fallback を使う
      }
    })();
    return () => { cancelled = true; };
  }, [appId, endpoint, anonKey]);

  // 履歴復元
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = sessionStorage.getItem(`chat_messages_${appId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Message[];
        setMessages(parsed);
        setUserMessageCount(parsed.filter((m) => m.role === "user").length);
      } catch { /* ignore */ }
    }
    if (sessionStorage.getItem(`chat_lead_captured_${appId}`) === "true") {
      setLeadCaptured(true);
    }
  }, [appId]);

  // 履歴保存
  useEffect(() => {
    if (typeof window === "undefined" || messages.length === 0) return;
    sessionStorage.setItem(`chat_messages_${appId}`, JSON.stringify(messages));
  }, [messages, appId]);

  // 自動スクロール
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent, showLeadForm]);

  // リードフォーム表示判定
  useEffect(() => {
    if (
      !leadCaptured &&
      userMessageCount >= config.leadCaptureAfterMessages &&
      !showLeadForm &&
      !isLoading
    ) {
      setShowLeadForm(true);
    }
  }, [userMessageCount, leadCaptured, config.leadCaptureAfterMessages, showLeadForm, isLoading]);

  const handleToggle = useCallback(() => {
    setIsOpen((o) => {
      const next = !o;
      if (next) setTimeout(() => inputRef.current?.focus(), 300);
      return next;
    });
  }, []);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const newMessages = [...messages, { role: "user", content: trimmed } as Message];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);
    setStreamingContent("");
    setUserMessageCount((c) => c + 1);

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${anonKey}`,
          apikey: anonKey,
        },
        body: JSON.stringify({ appId, messages: newMessages, sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("Chat API error");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.done) continue;
            if (data.content) {
              accumulated += data.content;
              setStreamingContent(accumulated);
            }
          } catch { /* incomplete chunk */ }
        }
      }

      if (accumulated) {
        setMessages((prev) => [...prev, { role: "assistant", content: accumulated }]);
      }
    } catch (err) {
      console.error("Chat error:", err);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "申し訳ございません。エラーが発生しました。しばらく経ってからもう一度お試しください。",
        },
      ]);
    } finally {
      setIsLoading(false);
      setStreamingContent("");
    }
  }, [input, isLoading, messages, appId, sessionId, endpoint, anonKey]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey && !(e.nativeEvent as any).isComposing) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  const handleLeadClose = useCallback(() => {
    setShowLeadForm(false);
    setLeadCaptured(true);
    sessionStorage.setItem(`chat_lead_captured_${appId}`, "true");
  }, [appId]);

  const styleTag = useMemo(
    () => `
    @keyframes gcw-bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-4px)} }
    .gcw-md p { margin: 4px 0; }
    .gcw-md ul, .gcw-md ol { margin: 4px 0; padding-left: 18px; }
    .gcw-md li { margin: 2px 0; }
    .gcw-md strong { color: #111827; }
    .gcw-md a { color: ${primary}; text-decoration: underline; }
  `,
    [primary],
  );

  return (
    <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 2147483000 }}>
      <style>{styleTag}</style>

      {/* パネル */}
      {isOpen && (
        <div
          style={{
            position: "fixed",
            bottom: 92,
            right: 20,
            width: 360,
            maxWidth: "calc(100vw - 40px)",
            height: 520,
            maxHeight: "calc(100vh - 120px)",
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
            border: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            fontFamily:
              "system-ui, -apple-system, 'Segoe UI', Roboto, 'Hiragino Sans', 'Noto Sans JP', sans-serif",
          }}
        >
          {/* ヘッダ */}
          <div
            style={{
              background: primary,
              color: "#fff",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {avatarUrl && (
                <img
                  src={avatarUrl}
                  alt=""
                  width={36}
                  height={36}
                  style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.3)", flexShrink: 0 }}
                />
              )}
              <div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{config.name}</p>
                <p style={{ margin: 0, fontSize: 11, opacity: 0.85 }}>オンライン</p>
              </div>
            </div>
            <button
              onClick={handleToggle}
              aria-label="閉じる"
              style={{
                width: 32, height: 32, borderRadius: "50%", border: "none",
                background: "transparent", cursor: "pointer", display: "flex",
                alignItems: "center", justifyContent: "center",
              }}
            >
              <IconClose />
            </button>
          </div>

          {/* メッセージ */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 12px", background: "#fff" }}>
            {messages.length === 0 && !streamingContent && (
              <AssistantBubble html={renderMarkdown(config.greeting)} avatar={avatarUrl} />
            )}
            {messages.map((m, i) =>
              m.role === "user" ? (
                <UserBubble key={i} content={m.content} color={primary} />
              ) : (
                <AssistantBubble key={i} html={renderMarkdown(m.content)} avatar={avatarUrl} />
              ),
            )}
            {streamingContent && <AssistantBubble html={renderMarkdown(streamingContent)} avatar={avatarUrl} streaming />}
            {isLoading && !streamingContent && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {avatarUrl && (
                  <img src={avatarUrl} alt="" width={28} height={28}
                    style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                )}
                <div style={{ display: "flex", gap: 4, padding: "8px 12px" }}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={{
                      width: 8, height: 8, borderRadius: "50%", background: "#9ca3af",
                      animation: `gcw-bounce 1s ${i * 0.15}s infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}
            {showLeadForm && (
              <LeadForm color={primary} contactEmail={contactEmail} name={config.name} onClose={handleLeadClose} />
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* 入力 */}
          <div style={{ borderTop: "1px solid #e5e7eb", padding: 12, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="メッセージを入力..."
                disabled={isLoading}
                style={{
                  flex: 1, padding: "10px 14px", fontSize: 14, borderRadius: 999,
                  border: "1px solid #d1d5db", outline: "none", background: "#f9fafb",
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                aria-label="送信"
                style={{
                  width: 40, height: 40, borderRadius: "50%", border: "none",
                  background: primary, cursor: input.trim() && !isLoading ? "pointer" : "default",
                  opacity: input.trim() && !isLoading ? 1 : 0.4, display: "flex",
                  alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
              >
                <IconSend />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* トグルボタン */}
      <button
        onClick={handleToggle}
        aria-label={isOpen ? "チャットを閉じる" : "AIチャットを開く"}
        style={{
          width: 56, height: 56, borderRadius: "50%", border: "none",
          background: isOpen || !avatarUrl ? primary : "#fff",
          color: "#fff", boxShadow: "0 8px 20px rgba(0,0,0,0.2)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", padding: 0,
        }}
      >
        {isOpen ? (
          <IconClose />
        ) : avatarUrl ? (
          <img src={avatarUrl} alt="" width={56} height={56}
            style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover" }} />
        ) : (
          <IconChat />
        )}
      </button>
    </div>
  );
}

function UserBubble({ content, color }: { content: string; color: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
      <div style={{
        maxWidth: "80%", background: color, color: "#fff", borderRadius: 16,
        borderBottomRightRadius: 4, padding: "10px 14px", fontSize: 14, lineHeight: 1.6,
        whiteSpace: "pre-wrap", wordBreak: "break-word",
      }}>
        {content}
      </div>
    </div>
  );
}

function AssistantBubble({ html, streaming, avatar }: { html: string; streaming?: boolean; avatar?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start", gap: 8, marginBottom: 12 }}>
      {avatar && (
        <img src={avatar} alt="" width={28} height={28}
          style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0, marginTop: 2 }} />
      )}
      <div style={{
        maxWidth: "85%", background: "#f3f4f6", color: "#1f2937", borderRadius: 16,
        borderBottomLeftRadius: 4, padding: "10px 14px", fontSize: 14, lineHeight: 1.6,
        wordBreak: "break-word",
      }}>
        <div className="gcw-md" dangerouslySetInnerHTML={{ __html: html }} />
        {streaming && (
          <span style={{
            display: "inline-block", width: 6, height: 14, marginLeft: 2,
            background: "#60a5fa", borderRadius: 2, verticalAlign: "middle",
            animation: "gcw-bounce 1s infinite",
          }} />
        )}
      </div>
    </div>
  );
}

function LeadForm({
  color, contactEmail, name, onClose,
}: { color: string; contactEmail: string; name: string; onClose: () => void }) {
  return (
    <div style={{
      margin: "12px 8px", background: "#eff6ff", border: "1px solid #bfdbfe",
      borderRadius: 12, padding: 16,
    }}>
      <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1e40af" }}>
        もっと詳しく知りたい方へ
      </p>
      <p style={{ margin: "4px 0 12px", fontSize: 12, color: "#2563eb" }}>
        製品の詳細やご相談は、お気軽にメールでお問い合わせください。
      </p>
      <a
        href={`mailto:${contactEmail}?subject=${encodeURIComponent(name + "についてのお問い合わせ")}`}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          width: "100%", background: color, color: "#fff", fontSize: 14, fontWeight: 600,
          padding: "10px 0", borderRadius: 8, textDecoration: "none",
        }}
      >
        メールで問い合わせる
      </a>
      <button
        onClick={onClose}
        style={{
          width: "100%", marginTop: 8, padding: "6px 0", fontSize: 12,
          color: "#6b7280", background: "transparent", border: "none", cursor: "pointer",
        }}
      >
        閉じる
      </button>
    </div>
  );
}

export default GenbaChatWidget;
